import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { loadTheme, saveTheme, type ThemeId } from '../lib/storage';

// ---------------------------------------------------------------------------
// useTheme — current theme + setter
// ---------------------------------------------------------------------------
//
// UI 刷新 (§7) で導入したテーマ切替。`<html data-theme="...">` 属性を更新する
// ことで :root[data-theme] 配下の CSS Variables が再カスケードし、アプリ全体が
// 即座に新しいテーマで再描画される。
//
// 永続化先は今のところ localStorage のみ。認証済みユーザー向けの Firestore
// 同期は将来の拡張ポイントとして空けてあり、`useTheme` の利用側 (App.tsx)
// から `setTheme` ハンドラ経由で差し込めばよい設計。

export interface UseThemeResult {
  readonly theme: ThemeId;
  readonly setTheme: (next: ThemeId) => void;
}

/**
 * 現在のテーマ ID と、テーマを変更する関数を返す。マウント時とテーマ変更時に
 * `<html data-theme="...">` を同期する副作用も持つ。
 */
export function useTheme(): UseThemeResult {
  const [theme, setThemeState] = useState<ThemeId>(loadTheme);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeId) => {
    setThemeState(next);
    saveTheme(next);
  }, []);

  return { theme, setTheme };
}

// ---------------------------------------------------------------------------
// ThemeSheetContext — テーマ設定シートを開閉する Context
// ---------------------------------------------------------------------------
//
// テーマ切替シートは App ツリーの最上位にマウントし、深い子孫
// (HamburgerMenu 等) から `useContext` で開けるようにする。シート本体の
// state は App.tsx が持ち、Context には open ハンドラだけを公開する。

export interface ThemeSheetContextValue {
  readonly open: () => void;
}

export const ThemeSheetContext = createContext<ThemeSheetContextValue | null>(null);

/**
 * 現在のテーマシート操作 API を返す。Provider 配下でない場合は `null`。
 * テーマ機能はオプショナルな扱いで、Provider が無い文脈 (テスト等) でも
 * クラッシュしないようにする。
 */
export function useThemeSheet(): ThemeSheetContextValue | null {
  return useContext(ThemeSheetContext);
}
